<template>
  <v-layout column>
    <MrxTriage />
    <div class="overall-assessment-care-plan">
      <div class="overall-assessment-treatments bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden mb-5">
        <v-card flat class="treatment-instructions flex align-center gap-2 px-5 py-3 border-b border-slate-100">
          <v-icon class="treatment-icon" size="14">{{ icons.mdiClipboard }}</v-icon>
          <mrx-translation token="app.treatments.instructions.title" class="text-bold"></mrx-translation>
        </v-card>
        <v-layout v-if="shouldShowTreatmentBlock" column id="treatment">
          <template
              v-for="triage in triages"
          >
            <template
                v-for="assessment in getAssessmentsWithTriage(triage)">
              <treatment-item
                  v-if="getTreatmentsForAssessment(assessment.name).length > 0"
                  :headerToken="riskAssessmentToken(assessment.name, assessment.severity)"
                  :key="`${assessment.name}_${assessment.severity}`"
                  :contentList="formatTreatments(assessment.name)"
                  :isChecklist="true"
                  :disabledItems="disabled"
                  v-model="selected"
                  @handleInput="setSelected"
              >
              </treatment-item>
            </template>
          </template>
        </v-layout>
        <v-layout v-else-if="shouldShowNoTreatmentsMessage" column id="treatment">
          <div class="p-4 treatment-row-container">
            <mrx-translation
              class="assessment-light treatment-text"
              token="app.careplan.message.no_treatments"
            ></mrx-translation>
          </div>
        </v-layout>
      </div>

      <div
          v-for="category in categories"
          :key="`care-plan-${category}`"
          class="overall-assessment-care-plan bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden mt-4"
      >
        <v-card flat class="treatment-instructions flex align-center gap-2 px-5 py-3 border-b border-slate-100">
          <mrx-translation :token="getHeaderToken(category)" class="text-bold"></mrx-translation>
        </v-card>
        <care-plan-item
            v-if="getContentList(category).length > 0"
            :contentList="getContentList(category)"
        ></care-plan-item>
      </div>

      <div class="overall-assessment-care-plan bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden mt-5">
        <v-card flat class="treatment-instructions flex align-center gap-2 px-5 py-3 border-b border-slate-100">
          <mrx-translation token="app.careplan.instructions.title" class="text-bold"></mrx-translation>
        </v-card>
        <v-layout column class="mx-4" id="followup-plan">
          <template
              v-for="triage in triages"
          >
            <template
                v-for="assessment in getAssessmentsWithTriage(triage)"
            >
              <care-plan-item
                  v-if="getFollowUpPlanForAssessment(assessment.name).length > 0"
                  :key="`follow-up-plan-${assessment.name}_${assessment.severity}`"
                  :headerToken="riskAssessmentToken(assessment.name, assessment.severity)"
                  :contentList="formatFollowUpPlanContent(assessment.name)"
              ></care-plan-item>
            </template>
          </template>
        </v-layout>
      </div>

      <div class="care-plan-additional-context-container bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden">
        <v-card flat class="treatment-instructions flex align-center gap-2 px-5 py-3 border-b border-slate-100 mb-4">
          <mrx-translation token="app.additional.context.title" class="text-bold"></mrx-translation>
        </v-card>

        <v-layout column class="mx-4 my-4 mt-0" id="followup-plan">
          <v-textarea
              :placeholder="$t('app.additional.context.placeholder')"
              class="careplan-additional-context" rows="4"
              hide-details
              solo
              v-model="additionalContext"
          />
        </v-layout>

      </div>

      <div
          class="d-flex flex-column align-center pt-2 pb-2"
          style="margin: 0 auto; width: 100%;"
      >
        <CopyClipboardButton
            token="app.careplan.button.copy_risk_assessments.title"
            name="careplan"
            cssClass="copy-all-btn"
            :payload="getCarePlanCopyPayload"
            mode="button"
            style="width: 100%;"
        />
      </div>

    </div>
  </v-layout>
</template>

<script lang="ts">
import {defineComponent, PropType} from "vue";


import languageLogic from "@/mixins/languageLogic";
import mrx from "@/mixins/mrx";
import icons from "@/mixins/icons";
import {mapState} from "pinia";
import {useMrxStore} from "@/stores/mrx";

import TreatmentItem from "@/features/overall-assessment/components/care-plan/TreatmentItem.vue";
import MrxTranslation from "@/components/thinkmd/mrx/translation.vue";
import CarePlanItem from "@/features/overall-assessment/components/care-plan/CarePlanItem.vue";
import CopyClipboardButton from "@/components/widgets/CopyClipboardButton.vue";
import {
  buildCarePlanCopyPayload,
  DEFAULT_CARE_PLAN_CATEGORIES,
  uniqueTreatmentsByName,
} from "@/utils/carePlanUtils";
import {
  buildRiskAssessmentCopySections,
  type CopyIdentifier,
  type CopySection,
} from "@/utils/encounterCopyUtils";
import { TRIAGE_ORDER } from "@/features/overall-assessment/constants/triage.constants";
import MrxTriage from "@/components/thinkmd/mrx/triage.vue";

export default defineComponent({
  name: 'OverallRiskAssessmentCarePlan',
  components: {MrxTriage, CopyClipboardButton, CarePlanItem, MrxTranslation, TreatmentItem },
  mixins: [languageLogic, mrx, icons],
  props: {
    copyIdentifiers: {
      type: Array as PropType<CopyIdentifier[]>,
      default: () => [],
    },
  },
  data() {
    return {
      selected: [],
      disabled: {},
      additionalContext: '',
      categories: [
        "perform",
        "treatment_instructions",
        "referral",
        "return_reevaluation"
      ],
    }
  },

  computed: {
    ...mapState(useMrxStore, {
      treatments: 'treatments',
      plans: 'plans',
      assessments: 'assessments',
      triageSelected: 'triageSelected',
      plansByCategory: 'getUniquePlansByCategory',
      selectedTreatments: 'getSelectedTreatments',
      hasTreatments: 'hasTreatmentsToShow'
    }),
    shouldShowTreatmentBlock() {
      if (!Array.isArray(this.triages) || this.triages.length === 0) {
        return false
      }

      return this.triages.some(triage => {
        return this.getAssessmentsWithTriage(triage).some(assessment => {
          return this.getTreatmentsForAssessment(assessment.name).length > 0
        })
      })
    },
    shouldShowNoTreatmentsMessage() {
      if (this.shouldShowTreatmentBlock) {
        return false
      }

      return ['urgent', 'immediate'].some(triage => {
        return this.getAssessmentsWithTriage(triage).length > 0
      })
    }
  },
  mounted() {
    window.scroll(0, 0)
    this.selected = this.plans().filter(b => b.selected).map(p => p.name)
    this.updateDisabledItems();  // Ensure the disabled items are initialized
  },
  watch: {
    selected: function() {
      useMrxStore().select({ type: 'plan', data: this.selected })
    },
  },
  methods: {
    getCarePlanCopyPayload() {
      const riskSections = buildRiskAssessmentCopySections({
        assessments: this.assessments(),
        triageOrder: TRIAGE_ORDER,
        translate: token => String(this.$t(token)),
        translationExists: token => this.$te(token),
        riskAssessmentToken: (name, severity) => this.riskAssessmentToken(name, severity),
      })

      return buildCarePlanCopyPayload({
        identifiers: this.copyIdentifiers,
        translate: token => String(this.$t(token)),
        riskSections,
        carePlanSections: this.getCarePlanCopySections(),
        additionalContext: this.additionalContext,
        additionalContextLabel: String(this.$t('app.additional.context.title')),
        riskTitle: String(this.$t('app.copy.section.risk_assessments')),
        carePlanTitle: String(this.$t('app.copy.section.care_plan')),
        rtl: this.$vuetify.rtl,
      })
    },
    getCarePlanCopySections(): CopySection[] {
      return DEFAULT_CARE_PLAN_CATEGORIES.map(category => {
        const contentList = category === 'medication'
          ? this.getMedicationCopyContent()
          : this.getNonMedicationContent(category)

        return {
          label: String(this.$t(this.getHeaderToken(category))),
          lines: contentList.flatMap(item => this.getCopyItemLines(category, item)),
        }
      }).filter(section => section.lines.length > 0)
    },
    getCopyItemLines(category: string, item): string[] {
      const heading = category === 'medication'
        ? item.headerToken
          ? String(this.$t(item.headerToken))
          : item.name || ''
        : item.subHeaderToken
          ? String(this.$t(item.subHeaderToken))
          : ''
      const details = Array.isArray(item.detailContent)
        ? item.detailContent.map(detail => String(this.$t(detail)))
        : []

      if (heading) {
        const nestedDetails = details.map(detail => `   • ${detail}`)
        return [[heading, ...nestedDetails].join('\n')]
      }

      return details
    },
    getMedicationCopyContent() {
      const selectedTreatments = uniqueTreatmentsByName(this.treatments(this.selected))
      const filteredTreatments = this.filterByGroupPriority(selectedTreatments)
      return this.formatTreatmentsCopyContent(filteredTreatments)
    },
    getHeaderToken(category: string) {
      if (category === 'medication') {
        return `care.category.treatment`
      }
      else {
        return `care.category.${category}`
      }
    },
    getContentList(category: string) {
      if (category === 'medication') {
        return this.getMedicationContent()
      }
      else {
        return this.getNonMedicationContent(category)
      }
    },
    getMedicationContent() {
      if (!this.hasTreatments) {
        return [{
          headerToken: 'app.careplan.message.no_treatments'
        }]
      }
      else if (!(this.selectedTreatments().length)) {
        return [{
          headerToken: 'app.careplan.message.no_treatments_selected'
        }]
      }
      else {
        const selectedTreatments = this.selectedTreatments()
        const filteredTreatments = this.filterByGroupPriority(selectedTreatments)
        return this.formatTreatmentsCopyContent(filteredTreatments)
      }
    },
    getNonMedicationContent(category : string) {
      const uniquePlans = this.plansByCategory(category)
      const filteredPlans = this.filterByGroupPriority(uniquePlans)
      return filteredPlans.map(plan => ({
        name: plan.name,
        detailContent: [`care.actions.${plan.name}`],
        useHTMLFormat: false
      }))
    },
    getAssessmentsWithTriage(triage) {
      const noAssessmentsSelected = this.triageSelected() === 'none_selected'
      return this.assessments().filter(a => {
        return a.triage_recommendation === triage && (a.selected || noAssessmentsSelected)
      })
    },
    setSelected(selected) {
      const previousSelected = this.selected
      const newlySelected = selected.filter(item => !previousSelected.includes(item))
      const deselected = previousSelected.filter(item => !selected.includes(item))

      newlySelected.forEach(treatmentName => {
        this.$track(this.$analyticsEvents.MEDICATION_SELECTED, {
          component: 'Treatment',
          medication_name: treatmentName,
          total_selected: selected.length
        })
      })

      deselected.forEach(treatmentName => {
        this.$track(this.$analyticsEvents.MEDICATION_SELECTED, {
          component: 'Treatment',
          medication_name: treatmentName,
          action: 'deselected',
          total_selected: selected.length
        })
      })

      this.selected = selected
      this.$nextTick(() => {
        this.updateDisabledItems();
      });
    },
    updateDisabledItems() {
      const currentDisablingFlags = new Set();
      const selectedTreatments = this.treatments(this.selected);

      selectedTreatments.forEach(treatment => {
        if (treatment.treatmentFlags) {
          treatment.treatmentFlags.forEach(flag => {
            currentDisablingFlags.add(flag.name)
          });
        }
      });

      // Set the disabled state based on shared treatment flags
      this.treatments().forEach(item => {
        if (!item.treatmentFlags) return;
        const disablingFlag = item.treatmentFlags.find(flag => {
          // current selected item is excluded so that it doesn't disable itself
          return currentDisablingFlags.has(flag.name) && !this.selected.includes(item.name)
        });

        // Store the disabled state and the token for the tooltip
        if (disablingFlag) {
          this.$set(this.disabled, item.name, {
            token: 'app.treatments.instructions.tooltip.options'
          });
        } else {
          this.$delete(this.disabled, item.name);
        }
      });
    },
    getTreatmentsForAssessment(assessment) {
      return this.treatments().filter(b => b.assessment === assessment)
    },
    formatTreatments(assessment) {
      const treatmentsForAssessment = this.getTreatmentsForAssessment(assessment)
      return treatmentsForAssessment.map(treatment => {
        const headerToken = `treatment.${treatment.name}`
        const content = this.convertTreatmentToString(treatment)
        return {
          detailContent: [content],
          headerToken: headerToken,
          name: treatment.name,
          treatmentFlags: treatment.treatmentFlags,
          useHTMLFormat: true,
          delivery: treatment.delivery
        }
      })
    },
    getFollowUpPlanForAssessment(assessment) {
      return this.plans().filter(b => b.assessment === assessment && b.category === 'follow_up_visit' && b.shown)
    },
    formatFollowUpPlanContent(assessment) {
      const treatmentsForAssessment = this.getFollowUpPlanForAssessment(assessment)
      return treatmentsForAssessment.map(plan => {
        const headerToken = `care.actions.${plan.name}`
        return {
          headerToken: headerToken,
          detailContent: [headerToken],
          name: plan.name,
          useHTMLFormat: true,
        }
      })
    },
    getHighestPriority(list) {
      return list.sort((a,b) => b.priority - a.priority)[0]
    },

    filterByGroupPriority(list) {
      const finalList = []
      const groups = {}
      list.forEach(item => {
        if (!item.group) finalList.push(item)
        else {
          groups[item.group] ||= []
          groups[item.group].push(item)
        }
      })
      const highestFromGroups = Object.values(groups).map(this.getHighestPriority)
      return finalList.concat(highestFromGroups)
    },
    formatTreatmentsCopyContent(treatments) {
      return treatments.map(treatment => {
        const content = this.convertTreatmentToString(treatment)

        return {
          detailContent: [content],
          headerToken: `treatment.${treatment.name}`,
          name: treatment.name,
          useHTMLFormat: true,
        }
      })
    },
  }

})
</script>
